import {
	IListAttrs,
	IListColumnConfig,
	IListOperations,
} from '@datachain/ui-sdk/common';
import { castDraft, immerable, produce } from 'immer';
import { Map, Set } from 'immutable';

import { EntityItemEntity, PersistenceState } from './entity-item.entity';

export interface IEntityListExtra extends IListColumnConfig<EntityItemEntity> {
	isFixed?: boolean;
	isSelectable?: boolean;
	isSearchable?: boolean;
	isInGroup?: boolean;
	isVisible?: boolean;
	canBeHidden?: boolean;
	canBeReordered?: boolean;
	canBeChosen?: boolean;
	format?: string;
	visibleIndex?: number;
	visible?: boolean;
	hasHeaderFilter?: boolean;
	width?: string | number;
	filterValue?: Array<string | number>;
	filterValues?: Array<string | number>;
	sortIndex?: number;
	sortOrder?: 'asc' | 'desc';
}

interface IEntityListEntity extends IListAttrs<EntityItemEntity> {
	availableColumns: Map<string, IEntityListExtra>;
}

const entityDefaults: IEntityListEntity = {
	elements: Map(),
	availableColumns: Map(),
};

export class EntityListEntity
	implements
		IEntityListEntity,
		IListOperations<EntityListEntity, EntityItemEntity>
{
	private [immerable] = true;
	public elements = entityDefaults.elements;
	public availableColumns = entityDefaults.availableColumns;

	public static build(
		availableCols: Array<IEntityListExtra>
	): EntityListEntity {
		const inst = new EntityListEntity();
		const cols = availableCols.reduce((acc, curr) => {
			acc.push([curr.field, curr]);
			return acc;
		}, new Array<[string, IEntityListExtra]>());
		inst.availableColumns = Map(cols);
		return inst;
	}

	private constructor() {}

	public getAvailableColumns(): Map<
		string,
		IListColumnConfig<EntityItemEntity>
	> {
		return this.availableColumns;
	}

	public setElements(elems: Map<string, EntityItemEntity>): EntityListEntity {
		return produce(this, (draft: EntityListEntity) => {
			draft.elements = elems;
		});
	}

	public setJob(id: number, value: boolean, jobId: string): EntityListEntity {
		return produce(this, (draft: EntityListEntity) => {
			const maybeFound = draft.elements.get(`${id}`);
			if (!maybeFound) {
				return;
			}
			/* if (maybeFound.isActionInProgress) {
				return;
			}*/
			draft.elements = draft.elements.set(
				`${id}`,
				maybeFound.setJobInProgress(value).setJobId(jobId)
			);
		});
	}

	public setJobId(id: number, value: string | null): EntityListEntity {
		return produce(this, (draft: EntityListEntity) => {
			const maybeFound = draft.elements.get(`${id}`);
			if (!maybeFound) {
				return;
			}
			draft.elements = draft.elements.set(`${id}`, maybeFound.setJobId(value));
		});
	}

	public updatePersistenceInfo(
		id: number,
		info: {
			isPersisted: boolean;
			partitionCount: number;
			persistenceDate: Date | null;
			persistenceDuration: number;
			persistenceSize: number;
		}
	): EntityListEntity {
		return produce(this, (draft: EntityListEntity) => {
			const maybeFound = draft.elements.get(`${id}`);
			if (!maybeFound) {
				return;
			}
			draft.elements = draft.elements.set(
				`${id}`,
				maybeFound.setPersistenceInfo(info)
			);
		});
	}

	public updateItem(
		entityId: number,
		charCount: number,
		depotCount: number
	): EntityListEntity {
		return produce(this, (draft: EntityListEntity) => {
			const maybeFound = draft.elements.get(`${entityId}`);
			if (!maybeFound) {
				return;
			}
			const updated = maybeFound
				.setCharCount(charCount)
				.setDepotCount(depotCount);
			draft.elements = draft.elements.set(`${entityId}`, updated);
		});
	}

	public extractUniqTags(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, entityItem) => {
			if (!entityItem.tags) {
				return acc;
			}
			return Set([
				...acc.toArray(),
				...entityItem.tags
					.toList()
					.toArray()
					.map((l) => l.label),
			]);
		}, Set<string>(startValues));
	}

	public extractModifiers(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, entityItem) => {
			if (entityItem.updatedByAvatar && entityItem.updatedByAvatar?.name) {
				acc = acc.add(entityItem.updatedByAvatar.name);
			}
			return acc;
		}, Set<string>(startValues));
	}

	public extractActionCreators(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, entityItem) => {
			if (entityItem.createdByAvatar && entityItem.createdByAvatar.name) {
				acc = acc.add(entityItem.createdByAvatar.name);
			}
			return acc;
		}, Set<string>(startValues));
	}

	public extractAvailableProjects(): Set<string> {
		return this.elements.reduce((acc, entityItem) => {
			if (entityItem.parentProject && entityItem.parentProject.label) {
				acc = acc.add(entityItem.parentProject.label);
			}
			return acc;
		}, Set<string>());
	}

	public updatePersistenceState(
		updates: Array<[number, PersistenceState]>
	): EntityListEntity {
		const updatedList = updates.reduce((list, curr) => {
			const [entityId, persistenceState] = curr;
			const currentItem = list.get(`${entityId}`);
			if (!currentItem) {
				return list;
			}
			const updatedItem =
				currentItem.updateEntityPersistenceState(persistenceState);
			return list.set(`${entityId}`, updatedItem);
		}, this.elements);
		return produce(this, (draft) => {
			draft.elements = castDraft(updatedList);
		});
	}
}
